import './ModelOverview.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  ActualVsPredictedDataInitial,
  CptbData,
  ModelOverviewData
} from '@blisspointmedia/bpm-types/dist/MMM';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { getGroupingValue } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { cptbAreaData, currencyFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer';
import { AreaChart } from '@Components/Charts/AreaChart';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { DynamicModelOverview } from '@Components/DynamicModelOverview/DynamicModelOverview';
import { InfoTooltip } from '@Components/InfoTooltip';
import { WidgetContainer } from '@Components/WidgetContainer';
import { Placement, Size } from '@Components/designConstants';
import { CONVERSION_FORMATTER_TYPE, CONVERSION_PRETTY_NAME } from '@Pages/MMM/mmm-utils';
import { isInternalSelector } from '../../../redux/user';
import { dateToMddyy } from '../../../utils/date-utils';
import { downloadJSONToCSV, downloadPNG, exportToExcel } from '../../../utils/download-utils';
import { ActualVsPredicted } from './ActualVsPredicted';
import { DATE_GROUPING_OPTIONS, DateGroupingKey } from './model-results-constants';

interface ModelOverviewProps {
  company: string;
  cptbDataDirect: CptbData[];
  cptbData: CptbData[];
  disabledPlatform: boolean | undefined;
  groupBy: string;
  kpi: string;
  kpiType: string;
  modelOverviewData: ModelOverviewData[];
  overallRoasCpa: number | undefined;
  actualVsPredictedData: ActualVsPredictedDataInitial[];
  showActualizedData: boolean;
}

interface RightData {
  kpi: string;
  mean_spend: number;
  refresh_date: string;
  test_end_date: string;
  test_rsquared: number;
  test_size: number;
  train_start_date: string;
  train_size: number;
  test_mape: number;
  test_mae?: number;
  'test_mae '?: number;
}
interface PrettyNameToDataMap {
  dataKey: string;
  name: string;
  value: string | number;
}

const DATA_KEYS = [
  'kpi',
  'mean_spend',
  'refresh_date',
  'test_end_date',
  'test_rsquared',
  'test_size',
  'train_start_date',
  'train_size',
  'test_mape',
  'test_mae ', // some old files have a space in the key
  'test_mae',
  'test_rsquared'
];

const formatKpiValue = (value: number, kpiType: string): string =>
  kpiType === 'revenue' ? formatMoney(value, 0) : formatNumber(value, 0);

const getOutOfSampleMae = (rightData: RightData, kpiType: string): string => {
  const maeWithSpace = rightData['test_mae '];
  if (maeWithSpace !== undefined) return formatKpiValue(maeWithSpace, kpiType);
  if (rightData.test_mae !== undefined) return formatKpiValue(rightData.test_mae, kpiType);
  return '';
};

const getRoasCpaValue = (overallRoasCpa: number | undefined, kpiType: string): string => {
  if (!overallRoasCpa) return '--';
  return kpiType === 'revenue' ? formatMoney(overallRoasCpa, 2) : formatNumber(overallRoasCpa);
};

export const ModelOverview = ({
  company,
  cptbData,
  cptbDataDirect,
  disabledPlatform,
  groupBy,
  kpi,
  kpiType,
  modelOverviewData,
  overallRoasCpa,
  actualVsPredictedData,
  showActualizedData
}: ModelOverviewProps) => {
  const [chartDateGrouping, setChartDateGrouping] = useState<DateGroupingKey>('Week');
  const [rightData, setRightData] = useState({} as RightData);
  const isInternal = useSelector(isInternalSelector);
  const [areaChartData, setAreaChartData] = useState(
    {} as { newD: any[]; areas: any[]; groupedAreas: any[] }
  );
  const [kpiChartYAxisWidth, setKpiChartYAxisWidth] = useState(0);
  const [spendChartYAxisWidth, setSpendChartYAxisWidth] = useState(0);
  const companyEqualsTest = TEST_COMPANIES.includes(company);

  // Maintain horizontal alignment between both stacked charts
  const maxYAxisWidth = useMemo(
    () => Math.max(kpiChartYAxisWidth, spendChartYAxisWidth),
    [kpiChartYAxisWidth, spendChartYAxisWidth]
  );

  const modelOverviewDataToExport: [string[], (string | number)[]] = useMemo(() => {
    if (Object.keys(rightData).length > 0 && kpi && overallRoasCpa) {
      const keyToPrettyName: PrettyNameToDataMap[] = [
        {
          dataKey: 'train_start_date',
          name: 'First model observation',
          value: dateToMddyy(rightData.train_start_date)
        },
        {
          dataKey: 'test_end_date',
          name: 'Last model observation',
          value: dateToMddyy(rightData.test_end_date)
        },
        {
          dataKey: 'mean_spend',
          name: 'Average weekly spend',
          value: currencyFormatter.format(rightData.mean_spend, 0)
        },
        {
          dataKey: 'kpi',
          name: 'Outcome variable',
          value: kpi
        },
        {
          dataKey: 'refresh_date',
          name: 'Last refresh date',
          value: dateToMddyy(rightData.refresh_date)
        },
        {
          dataKey: 'N/A',
          name: 'Train/Test split',
          value: `${(rightData.train_size * 100).toFixed(0)}/${(rightData.test_size * 100).toFixed(
            0
          )}`
        },
        {
          dataKey: 'test_mape',
          name: 'Out-of-sample MAPE',
          value: `${rightData.test_mape.toFixed(1)}%`
        },
        {
          dataKey: 'test_mae',
          name: 'Out-of-sample MAE',
          value: getOutOfSampleMae(rightData, kpiType)
        },
        {
          dataKey: 'overallRoasCpa',
          name:
            kpiType === 'revenue' ? 'Trailing Twelve Months ROAS' : 'Trailing Twelve Months CPA',
          value: overallRoasCpa || ''
        }
      ];

      return keyToPrettyName.reduce(
        (acc, cv) => {
          acc[0].push(cv.name);
          acc[1].push(cv.value);
          return acc;
        },
        [[], []] as [string[], (string | number)[]]
      );
    }
    return [[], []];
  }, [rightData, kpi, kpiType, overallRoasCpa]);

  useEffect(() => {
    if (cptbDataDirect.length > 0) {
      const filtered = cptbDataDirect.filter(item => item.name !== 'Covariates');
      const data = cptbAreaData({
        fiscalCal: {},
        data: filtered,
        data1Key: cptbDataDirect[0].raw_spend !== undefined ? 'raw_spend' : 'direct_spend',
        groupBy,
        dateGrouping: chartDateGrouping,
        disabledPlatform,
        companyEqualsTest
      });

      setAreaChartData(data);
    }
  }, [
    chartDateGrouping,
    companyEqualsTest,
    cptbDataDirect,
    disabledPlatform,
    groupBy,
    showActualizedData,
    cptbData
  ]);

  useEffect(() => {
    if (modelOverviewData.length > 0) {
      const rightUsedData = DATA_KEYS.map(key =>
        modelOverviewData.find(data => key === data.parameter)
      );

      const rightDataAsObject: Record<string, unknown> = {};
      rightUsedData.forEach(data => {
        if (data) {
          rightDataAsObject[data.parameter] = data.value;
        }
      });
      setRightData(rightDataAsObject as unknown as RightData);
    }
  }, [modelOverviewData]);

  const excelDownloadRight = useCallback(() => {
    exportToExcel(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const csvDownloadRight = useCallback(() => {
    downloadJSONToCSV(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const excelDownloadActVPred = useCallback(() => {
    exportToExcel(actualVsPredictedData, `${company}_actual_vs_predicted`);
  }, [actualVsPredictedData, company]);

  const pngDownloadActVPred = useCallback(async () => {
    await downloadPNG('.actual-vs-predicted-graph', `${company}_actual_vs_predicted`);
  }, [company]);

  const excelDownloadAreaGraph = useCallback(() => {
    const XLSX_KEYS = [
      'client',
      'kpi',
      'refresh_date',
      'name',
      'date',
      'raw_spend',
      'direct_spend'
    ];
    const cptbDataDirectToExport = cptbDataDirect.map(item => {
      const picked: Record<string, any> = {};
      XLSX_KEYS.forEach(key => {
        if (key in item) {
          picked[key] = (item as Record<string, any>)[key];
        }
      });
      return picked;
    });

    exportToExcel(cptbDataDirectToExport, `${company}_area_spend`);
  }, [cptbDataDirect, company]);

  const pngDownloadAreaGraph = useCallback(async () => {
    await downloadPNG('.rmmm-model-overview .area-chart', `${company}_area`);
  }, [company]);

  const getActVPredDataByDateGrouping = (data: any[], dateGrouping: DateGroupingKey) => {
    const totalsByDate: Record<string, Record<string, number>> = {};
    for (const item of data) {
      const { date, actual_kpi, predicted_kpi } = item;

      const dateToUse = getGroupingValue(date, dateGrouping, {});
      totalsByDate[dateToUse] = {
        actual_kpi: (totalsByDate[dateToUse]?.actual_kpi ?? 0) + actual_kpi,
        predicted_kpi: (totalsByDate[dateToUse]?.predicted_kpi ?? 0) + predicted_kpi
      };
    }

    return Object.keys(totalsByDate).map(dateKey => ({
      date: dateKey,
      actual_kpi: totalsByDate[dateKey].actual_kpi,
      predicted_kpi: totalsByDate[dateKey].predicted_kpi
    }));
  };

  const actVPredDataByDate = getActVPredDataByDateGrouping(
    actualVsPredictedData,
    chartDateGrouping
  );

  return (
    <WidgetContainer
      collapsible
      header='How well does the model understand our business?'
      subHeader={
        <>
          High out-of-sample prediction accuracy is indicative of reliable causal understanding.
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            The model is estimated by splitting up the time series data into slices called “train”
            and “test.” The training data is used to estimate the relationships between inputs
            (media investments, contextual variables) and outputs (the KPI). The test data is used
            to evaluate the performance of the trained model “out of sample,” i.e. on observations
            it has not already encountered.
            <br />
            <br /> Out-of-sample predictive accuracy is a critical concept because it proxies what
            we ultimately care about, namely an understanding of likely KPI outputs given a
            combination of novel media inputs. See the out-of-sample statistics at right for
            measures of this concept.
          </InfoTooltip>
        </>
      }
    >
      {Object.keys(rightData).length > 0 && (
        <div className='rmmm-model-overview'>
          <div className='left'>
            <ChartContainer
              minHeight={216}
              rightActions={
                <DownloadDropdown onClickOptions={[excelDownloadActVPred, pngDownloadActVPred]} />
              }
              title={
                <>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={chartDateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={option => setChartDateGrouping(option)}
                  />
                  <div>{`Historical ${CONVERSION_PRETTY_NAME[kpiType]}`}</div>
                  <div className='header-text'>(Actual vs Predicted)</div>
                </>
              }
            >
              {actVPredDataByDate.length > 0 && (
                <ActualVsPredicted
                  data={actVPredDataByDate}
                  dateGrouping={chartDateGrouping}
                  legendValue={CONVERSION_PRETTY_NAME[kpiType]}
                  syncId={1}
                  tooltipFormatter={val =>
                    val < 1000
                      ? CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)
                      : CONVERSION_FORMATTER_TYPE[kpiType].format(val, 1)
                  }
                  yAxisFormatter={val => CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)}
                  yAxisStaticWidth={maxYAxisWidth}
                  setChartYAxisDynamicWidth={setKpiChartYAxisWidth}
                />
              )}
            </ChartContainer>
            <ChartContainer
              minHeight={405}
              title={
                <>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={chartDateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={option => setChartDateGrouping(option)}
                  />
                  <div>Historical Spend</div>
                </>
              }
              rightActions={
                <DownloadDropdown onClickOptions={[excelDownloadAreaGraph, pngDownloadAreaGraph]} />
              }
            >
              {Object.keys(areaChartData).length > 0 && (
                <div className='model-overview-spend'>
                  <AreaChart
                    areas={areaChartData.areas}
                    data={areaChartData.newD}
                    dateGrouping={chartDateGrouping}
                    groupedAreas={areaChartData.groupedAreas}
                    incrementality={false}
                    syncId={1}
                    tooltipFormatter={val =>
                      val < 1000
                        ? currencyFormatter.format(val, 0)
                        : currencyFormatter.format(val, 1)
                    }
                    xAxisDataKey='date'
                    xAxisTickFormatter={val => dateToMddyy(val)}
                    yAxisTickFormatter={val => currencyFormatter.format(val, 0)}
                    yAxisStaticWidth={maxYAxisWidth}
                    setChartYAxisDynamicWidth={setSpendChartYAxisWidth}
                  />
                </div>
              )}
            </ChartContainer>
          </div>
          <div className='right'>
            <DynamicModelOverview
              sections={[
                {
                  title: 'Training Data',
                  rows: [
                    {
                      label: 'First model observation',
                      value: dateToMddyy(rightData.train_start_date)
                    },
                    {
                      label: 'Last model observation',
                      value: dateToMddyy(rightData.test_end_date)
                    },
                    {
                      label: 'Average weekly spend',
                      value: currencyFormatter.format(rightData.mean_spend, 0)
                    },
                    { label: 'Outcome variable', value: kpi.replace(/_/g, ' ') }
                  ]
                },
                {
                  title: 'Recency',
                  rows: [{ label: 'Last refresh date', value: dateToMddyy(rightData.refresh_date) }]
                },
                {
                  title: 'Statistics',
                  rows: [
                    {
                      label: 'Train/Test split',
                      value: `${(rightData.train_size * 100).toFixed(0)}/${(
                        rightData.test_size * 100
                      ).toFixed(0)}`,
                      tooltipText:
                        'This refers to the fraction of data used to fit the model vs the fraction held out for model validation. Setting aside data for model validation is very important in ensuring that the model generalizes well to new, unseen data.'
                    },
                    ...(isInternal && !companyEqualsTest
                      ? [
                          {
                            label: (
                              <>
                                Out-of-sample R<sup>2</sup>
                              </>
                            ),
                            value: rightData.test_rsquared.toFixed(2),
                            tooltipText:
                              "The percentage of variation in the unseen test data outcomes that is accounted for by the model's explanatory variables. Formally, this is 1 - (residual sum of squares) / (total sum of squares)"
                          }
                        ]
                      : []),
                    {
                      label: 'Out-of-sample MAPE',
                      value: `${rightData.test_mape.toFixed(1)}%`,
                      tooltipText:
                        "Mean absolute percentage error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in percentage terms. Its definition is avg(abs((predicted - actual)/actual))."
                    },
                    {
                      label: 'Out-of-sample MAE',
                      value: getOutOfSampleMae(rightData, kpiType),
                      tooltipText:
                        "Mean absolute error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in absolute terms. Its definition is avg(abs(predicted - actual))."
                    }
                  ]
                },
                {
                  title: 'Key Results',
                  rows: [
                    {
                      label:
                        kpiType === 'revenue'
                          ? 'Trailing Twelve Months ROAS'
                          : 'Trailing Twelve Months CPA',
                      value: getRoasCpaValue(overallRoasCpa, kpiType)
                    }
                  ]
                }
              ]}
              rightActions={
                <DownloadDropdown
                  onClickOptions={[excelDownloadRight, csvDownloadRight]}
                  menuOptions={['XLSX', 'CSV']}
                />
              }
            />
          </div>
        </div>
      )}
    </WidgetContainer>
  );
};
